import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { database } from "../../config";
import { primary } from "../../ui/colors";

const COLLECTION = "compartirGrupoCon";

// Each group has a Deseo/Deseamos choice and one text field. Only the key that
// matches the selected choice is saved, e.g. "Deseo" + "viajar con" ->
// deseoCompartir.
const GROUPS = [
  {
    id: "compartir",
    title: "Deseo/Deseamos viajar con",
    placeholder: "Viajar con",
    keys: { deseo: "deseoCompartir", deseamos: "deseamosCompartir" },
  },
  {
    id: "noCompartir",
    title: "Deseo/Deseamos NO viajar con",
    placeholder: "No viajar con",
    keys: { deseo: "noDeseoCompartir", deseamos: "noDeseamosCompartir" },
  },
];

const CHOICES = [
  { id: "deseo", label: "Deseo" },
  { id: "deseamos", label: "Deseamos" },
];

const EMPTY_VALUES = GROUPS.reduce(
  (acc, g) => ({ ...acc, [g.id]: { choice: "deseo", text: "" } }),
  {}
);

// Builds the form state from a saved document: whichever key has a value
// decides the checked option.
const valuesFromDoc = (data) =>
  GROUPS.reduce((acc, g) => {
    const deseo = data?.[g.keys.deseo];
    const deseamos = data?.[g.keys.deseamos];
    if (deseo) return { ...acc, [g.id]: { choice: "deseo", text: deseo } };
    if (deseamos)
      return { ...acc, [g.id]: { choice: "deseamos", text: deseamos } };
    return { ...acc, [g.id]: EMPTY_VALUES[g.id] };
  }, {});

// Only the selected key is written, and only when the text is not empty.
const docFromValues = (values) =>
  GROUPS.reduce((acc, g) => {
    const { choice, text } = values[g.id];
    const trimmed = text.trim();
    return trimmed ? { ...acc, [g.keys[choice]]: trimmed } : acc;
  }, {});

const ShareGroupPreferences = ({ passengerId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [values, setValues] = useState(EMPTY_VALUES);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState();
  const [error, setError] = useState();

  useEffect(() => {
    if (passengerId == null) return;
    setIsLoading(true);
    getDoc(doc(database, COLLECTION, String(passengerId)))
      .then((snap) => {
        if (snap.exists()) setValues(valuesFromDoc(snap.data()));
      })
      .catch((e) => {
        console.log(e);
        setError("No se pudieron cargar los datos");
      })
      .finally(() => setIsLoading(false));
  }, [passengerId]);

  const handleChange = (groupId, changes) => {
    setValues((prev) => ({
      ...prev,
      [groupId]: { ...prev[groupId], ...changes },
    }));
    setMessage();
  };

  const handleSave = () => {
    setIsSaving(true);
    setError();
    setMessage();
    // No merge: replacing the whole document drops the key of the option that
    // is no longer selected.
    setDoc(
      doc(database, COLLECTION, String(passengerId)),
      docFromValues(values)
    )
      .then(() => setMessage("Guardado"))
      .catch((e) => {
        console.log(e);
        setError("Ocurrio un error al guardar");
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <div style={styles.wrapper}>
      <button
        type="button"
        style={styles.header}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>
          Solicitudes para compartir/no compartir el viaje con otros
          grupos/pasajeros
        </span>
        <svg
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
          style={{
            flexShrink: 0,
            transition: "transform 0.15s",
            transform: isOpen ? "rotate(180deg)" : "none",
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div style={styles.container}>
          {GROUPS.map((g) => (
            <div key={g.id} style={styles.group}>
              <span style={styles.label}>{g.title}</span>
              <select
                style={styles.select}
                value={values[g.id].choice}
                onChange={(e) => handleChange(g.id, { choice: e.target.value })}
                disabled={isLoading || isSaving}
              >
                {CHOICES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
              <input
                style={styles.input}
                value={values[g.id].text}
                onChange={(e) => handleChange(g.id, { text: e.target.value })}
                disabled={isLoading || isSaving}
                placeholder={isLoading ? "Cargando..." : g.placeholder}
              />
            </div>
          ))}

          <button
            style={{
              ...styles.button,
              opacity: isLoading || isSaving ? 0.6 : 1,
            }}
            onClick={handleSave}
            disabled={isLoading || isSaving}
          >
            {isSaving ? "Guardando..." : "GUARDAR"}
          </button>

          {message && <div style={styles.message}>{message}</div>}
          {error && <div style={styles.error}>{error}</div>}
        </div>
      )}
    </div>
  );
};

export default ShareGroupPreferences;

const styles = {
  wrapper: {
    border: "1.5px solid #e0e0e0",
    borderRadius: 8,
    overflow: "hidden",
  },
  header: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "10px 12px",
    border: "none",
    background: "#f7f7f7",
    color: "#111",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Gilroy",
    textAlign: "left",
    cursor: "pointer",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: 12,
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  // Same look as the trip selector in TripConfirmation.
  select: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1.5px solid #e0e0e0",
    fontSize: 14,
    fontFamily: "Gilroy",
    background: "#fff",
  },
  label: {
    fontSize: 13,
    color: "#444",
    fontWeight: 600,
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1.5px solid #e0e0e0",
    fontSize: 14,
    fontFamily: "Gilroy",
    background: "#fff",
  },
  button: {
    padding: "8px 16px",
    border: "none",
    borderRadius: 8,
    background: primary,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "Gilroy-Bold",
  },
  message: {
    fontSize: 12,
    color: "#00a896",
    fontWeight: 600,
  },
  error: {
    fontSize: 12,
    color: "#c0392b",
  },
};
